import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleAuthCallback = async () => {
      try {
        // Check for error in URL params
        const params = new URLSearchParams(location.search);
        const hashParams = new URLSearchParams(location.hash.substring(1));
        
        const error = params.get('error') || hashParams.get('error');
        if (error) {
          console.error('OAuth error:', error, params.get('error_description'));
          navigate('/', { replace: true });
          return;
        }

        // Check for authorization code (PKCE flow)
        const code = params.get('code');
        
        if (code) {
          // Exchange the code for a session
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          
          if (exchangeError) {
            console.error('Code exchange error:', exchangeError);
            navigate('/', { replace: true });
            return;
          }
          
          if (data.session) {
            console.log('Auth successful via code exchange, redirecting to dashboard');
            navigate('/dashboard', { replace: true });
            return;
          }
        }

        // Check for access_token in hash (implicit flow fallback)
        const accessToken = hashParams.get('access_token');
        if (accessToken) {
          // Session should be automatically set by Supabase
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            console.log('Auth successful via hash, redirecting to dashboard');
            navigate('/dashboard', { replace: true });
            return;
          }
        }

        // Final fallback - check if session exists
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('Session found, redirecting to dashboard');
          navigate('/dashboard', { replace: true });
        } else {
          console.log('No session found after callback');
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/', { replace: true });
      }
    };

    handleAuthCallback();
  }, [navigate, location]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
